import { useCallback, useState, useEffect } from 'react';

import { BsXLg, BsArrowCounterclockwise } from 'react-icons/bs';

import { sendAgenticMessage, fetchPreviewResults } from '../../api/agenticSearchApi';
import { useLeadData } from '../../contexts/LeadDataContext';
import { useExecuteAgenticSearch } from '../../models/prospectQueries/mutations';
import { useAgenticSearchStore } from '../../stores/agenticSearchStore';

import AgenticChatInput from './AgenticChatInput';
import AgenticChatMessages from './AgenticChatMessages';
import AgenticChatToggle from './AgenticChatToggle';

const GREETING_MESSAGE =
	'Hallo! Ich helfe Ihnen, die passenden Unternehmen zu finden. Beschreiben Sie einfach, welche Art von Firmen Sie suchen - z.B. Branche, Standort, Gr\u00F6\u00DFe oder besondere Kriterien.';

/**
 * Main container for the agentic search chat widget.
 * Renders as a floating panel anchored to the bottom-right.
 */
const AgenticSearchChat = () => {
	const {
		isOpen,
		setIsOpen,
		messages,
		addMessage,
		isStreaming,
		streamingContent,
		setStreaming,
		appendStreamContent,
		extractedParams,
		setExtractedParams,
		previewResults,
		setPreviewResults,
		isLoadingPreview,
		setIsLoadingPreview,
		phase,
		setPhase,
		resetChat,
		getMessagesForApi,
	} = useAgenticSearchStore();

	const { handleSearchQuerySelect } = useLeadData();
	const executeAgenticSearchMutation = useExecuteAgenticSearch();
	const [isExecuting, setIsExecuting] = useState(false);

	// Initialize chat with greeting on first open
	useEffect(() => {
		if (isOpen && phase === 'idle') {
			addMessage({ role: 'assistant', content: GREETING_MESSAGE, type: 'text' });
			setPhase('conversing');
		}
	}, [isOpen, phase, addMessage, setPhase]);

	// Load HR preview synchronously
	const loadPreview = useCallback(
		async (params) => {
			setIsLoadingPreview(true);
			try {
				const result = await fetchPreviewResults(params);
				setPreviewResults(result.companies);
				setPhase('previewing');
			} catch (err) {
				console.error('Preview error:', err);
				addMessage({
					role: 'assistant',
					content: 'Die Vorschau konnte leider nicht geladen werden. Bitte versuchen Sie es erneut.',
					type: 'text',
				});
				setPhase('conversing');
			} finally {
				setIsLoadingPreview(false);
			}
		},
		[setIsLoadingPreview, setPreviewResults, setPhase, addMessage],
	);

	// Send a user message
	const handleSendMessage = useCallback(
		async (content) => {
			if (isStreaming) return;

			addMessage({ role: 'user', content, type: 'text' });
			setStreaming(true);

			const cleanContent = (text) => text.replace(/<search_params>[\s\S]*?<\/search_params>/g, '').trim();

			let fullResponse = '';
			let receivedParams = null;

			await sendAgenticMessage({
				messages: getMessagesForApi(),
				content,
				onChunk: (chunk) => {
					fullResponse += chunk;
					appendStreamContent(chunk);
				},
				onSearchParams: (params) => {
					receivedParams = params;
					setExtractedParams(params);
				},
				onComplete: () => {
					setStreaming(false);
					const cleaned = cleanContent(fullResponse);
					if (cleaned) {
						addMessage({ role: 'assistant', content: cleaned, type: 'text' });
					}

					if (receivedParams) {
						if (receivedParams.searchType === 'hr') {
							// HR: load synchronous preview (3 results)
							loadPreview(receivedParams);
						} else {
							// Maps: no preview, show direct approve buttons
							setPhase('awaiting_approval');
						}
					}
				},
				onSearchParamsError: (errorMessage) => {
					addMessage({
						role: 'assistant',
						content: `Die Suchparameter konnten nicht verarbeitet werden: ${errorMessage}\n\nBitte passen Sie Ihre Kriterien an.`,
						type: 'text',
					});
				},
				onError: (err) => {
					setStreaming(false);
					console.error('Agentic chat error:', err);
					addMessage({
						role: 'assistant',
						content: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
						type: 'text',
					});
				},
			});
		},
		[
			isStreaming,
			addMessage,
			setStreaming,
			appendStreamContent,
			setExtractedParams,
			loadPreview,
			setPhase,
			getMessagesForApi,
		],
	);

	// User approves → execute full search
	const handleApprove = useCallback(async () => {
		if (!extractedParams || isExecuting) return;

		setIsExecuting(true);
		try {
			// Selecting the new query only sticks once the refetched list contains it —
			// useSearchQueryManagement resets the selection to searchQueries[0] otherwise.
			const result = await executeAgenticSearchMutation.mutateAsync({ searchParams: extractedParams });

			addMessage({
				role: 'assistant',
				content: 'Suche wird gestartet! Die Ergebnisse erscheinen in K\u00FCrze in Ihrer Lead-\u00DCbersicht.',
				type: 'text',
			});

			setPhase('completed');
			setPreviewResults(null);
			handleSearchQuerySelect(result.prospectQueryId);

			setTimeout(() => setIsOpen(false), 1500);
		} catch (err) {
			addMessage({
				role: 'assistant',
				content: `Fehler beim Starten der Suche: ${err.message || 'Unbekannter Fehler'}`,
				type: 'text',
			});
		} finally {
			setIsExecuting(false);
		}
	}, [
		extractedParams,
		isExecuting,
		executeAgenticSearchMutation,
		addMessage,
		setPhase,
		setPreviewResults,
		handleSearchQuerySelect,
		setIsOpen,
	]);

	// User wants to refine
	const handleRefine = useCallback(() => {
		setPreviewResults(null);
		setExtractedParams(null);
		setPhase('conversing');
		addMessage({
			role: 'assistant',
			content: 'Kein Problem! Was m\u00F6chten Sie \u00E4ndern?',
			type: 'text',
		});
	}, [setPreviewResults, setExtractedParams, setPhase, addMessage]);

	// Reset conversation
	const handleReset = useCallback(() => {
		resetChat();
		addMessage({ role: 'assistant', content: GREETING_MESSAGE, type: 'text' });
		setPhase('conversing');
	}, [resetChat, addMessage, setPhase]);

	const inputDisabled =
		isStreaming || isLoadingPreview || phase === 'completed' || phase === 'awaiting_approval' || isExecuting;

	return (
		<>
			<AgenticChatToggle />

			{isOpen && (
				<div className="fixed bottom-24 right-6 z-50 w-[400px] h-[550px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
					<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-purple-600 text-white rounded-t-xl">
						<div className="flex items-center gap-2">
							<span className="text-sm font-semibold">KI-Suche</span>
							{phase !== 'idle' && phase !== 'conversing' && (
								<span className="text-xs bg-purple-500 px-2 py-0.5 rounded-full">
									{phase === 'awaiting_approval'
										? 'Bereit'
										: phase === 'previewing'
											? 'Vorschau'
											: phase === 'executing'
												? 'Suche l\u00E4uft'
												: 'Abgeschlossen'}
								</span>
							)}
						</div>
						<div className="flex items-center gap-1">
							<button
								onClick={handleReset}
								className="p-1.5 hover:bg-purple-500 rounded-lg transition-colors"
								title="Neue Suche starten"
							>
								<BsArrowCounterclockwise className="w-4 h-4" />
							</button>
							<button
								onClick={() => setIsOpen(false)}
								className="p-1.5 hover:bg-purple-500 rounded-lg transition-colors"
								title="Schlie\u00DFen"
							>
								<BsXLg className="w-3.5 h-3.5" />
							</button>
						</div>
					</div>

					{/* Messages */}
					<AgenticChatMessages
						messages={messages}
						isStreaming={isStreaming}
						streamingContent={streamingContent}
						previewResults={previewResults}
						isLoadingPreview={isLoadingPreview}
						onApprove={handleApprove}
						onRefine={handleRefine}
						isExecuting={isExecuting}
						phase={phase}
					/>

					{/* Input */}
					<AgenticChatInput onSend={handleSendMessage} disabled={inputDisabled} />
				</div>
			)}
		</>
	);
};

export default AgenticSearchChat;
